<script setup lang="ts">
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { computed, ref } from "vue";
import CoincidencesList from "../../../components/CoincidencesList.vue";
import ProviderFiltersSlideOver from "../../../components/ProviderFiltersSlideOver.vue";
import GroupList from "./components/GroupList.vue";
import useHotelGroups from "./composables/useHotelGroup";
import useHotelGroupForm from "./composables/useHotelGroupForm";
import useSearchHotelGroups from "./composables/useSearchHotelGroups";

const { data, debouncedSetQuery, refetch, page, isLoading, filters } =
  useSearchHotelGroups({
    immediate: true,
    queryKey: "list-hotel-groups",
  });

const {
  actionModal,
  modalAction,
  form,
  handleEvaluation,
  handleCreate,
  clearState,
  isCoincidence,
  createMutationLoading,
  hotels,
} = useHotelGroupForm();

const { deleteMutationAsync, restoreMutationAsync } = useHotelGroups({
  immediate: false,
});

const pages = ref<Page[]>([
  {
    name: "Proveedores de hoteleria",
    to: {
      name: "hotel",
    },
  },
  {
    name: "Grupos",
    to: {
      name: "hotel.groups",
    },
  },
]);

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = newPage;
    refetch();
  }
};

const handleDelete = async (id: number) => {
  await deleteMutationAsync(id);
  refetch();
};

const handleRestore = async (id: number) => {
  await restoreMutationAsync(id);
  refetch();
};

// Slide over filters
const isFiltersOpen = ref(false);

const filtersActive = computed(() => {
  let count = 0;
  if (filters.active !== undefined) count++;
  return count > 0 ? `(${count})` : "";
});

const resetFilters = () => {
  filters.orderBy = "name";
  filters.orderDirection = "asc";
  filters.active = undefined;
};
</script>

<template>
  <BreadCrumb :pages="pages" />

  <main class="mx-auto max-w-7xl px-4 py-6">
    <!-- Header -->
    <div class="mb-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Grupos</h1>
          <p class="mt-1 text-sm text-gray-500">
            Organiza hoteles en grupos para gestionar tarifas especiales
          </p>
        </div>

        <HasPermission
          :name-permission="'create-internal'"
          :entity-type="'App\\Models\\Group'"
        >
          <StyledButton
            class="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
            @click="actionModal"
          >
            <span class="material-symbols-outlined text-xl">add</span>
            Nuevo grupo
          </StyledButton>
        </HasPermission>
      </div>

      <!-- Filters button -->
      <div class="mt-4 flex items-center gap-3">
        <button
          @click="isFiltersOpen = true"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow"
        >
          <span class="material-symbols-outlined text-lg">filter_alt</span>
          Filtros {{ filtersActive }}
        </button>
      </div>
    </div>

    <!-- List -->
    <GroupList
      :data="data?.data || []"
      :is-loading="isLoading"
      @delete="handleDelete"
      @restore="handleRestore"
      @action-modal="actionModal"
    >
      <template #pagination>
        <div class="mt-4 flex justify-end" v-if="data?.data?.length">
          <PaginateNav
            :page="page"
            :pagination="data"
            :data="data?.data || []"
            :handle-page="handlePage"
          />
        </div>
      </template>
    </GroupList>
  </main>

  <!-- Filters SlideOver -->
  <ProviderFiltersSlideOver
    :when="isFiltersOpen"
    :filters="filters"
    title="Filtros de grupos"
    icon="workspaces"
    icon-bg-color="bg-emerald-100"
    icon-color="text-emerald-600"
    button-color="bg-emerald-600 hover:bg-emerald-700"
    @close="isFiltersOpen = false"
    @search="debouncedSetQuery"
    @update:order-by="(v) => (filters.orderBy = v)"
    @update:order-direction="(v) => (filters.orderDirection = v)"
    @update:active="(v) => (filters.active = v)"
    @reset="resetFilters"
  />

  <!-- Modal create -->
  <ModalComponent
    :model-value="modalAction"
    @update:model-value="
      () => {
        actionModal();
      }
    "
  >
    <template #modalBody>
      <div class="p-6">
        <h2 class="mb-6 text-xl font-semibold text-gray-900">Crear nuevo grupo</h2>

        <form @submit.prevent="handleEvaluation" class="space-y-4">
          <StyledInput
            v-model="form.name"
            label="Nombre del grupo"
            placeholder="Ingresa el nombre del grupo"
            :is-required="true"
            :focus="true"
            :disabled="isCoincidence"
          />

          <StyledButton
            v-if="!isCoincidence"
            type="submit"
            class="w-full rounded-xl bg-blue-600 py-3 text-white hover:bg-blue-700"
            :loading="createMutationLoading"
          >
            Crear grupo
          </StyledButton>

          <CoincidencesList
            :data="hotels?.data || []"
            :is-coincidence="isCoincidence"
            :loading-creating="createMutationLoading"
            :handle-action="handleCreate"
            :clear-state="clearState"
            :action-modal="actionModal"
          />
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
